import React, { useRef, useState } from "react";
import axios from "axios";
import {
  Moon,
  PersonStanding,
  Apple,
  HeartPulse,
  Sparkles,
  BookOpen,
  Rocket,
  Bell,
  Star,
  Lock,
  User,
  Mail,
  Volume2,
  VolumeX,
} from "lucide-react";
import { Toaster, toast } from "sonner";

import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AppleHealthMark,
  OuraMark,
  WhoopMark,
  FitbitMark,
  GarminMark,
  DexcomMark,
  WithingsMark,
} from "../components/WearableLogos";
import { SocialProofFlash } from "../components/SocialProofFlash";
import { RotatingAvatars } from "../components/RotatingAvatars";
import { HeadlineFader } from "../components/HeadlineFader";
import { SharedHeader } from "../components/SharedHeader";
import { SiteFooter } from "../components/SiteFooter";
import { LANDING } from "../constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_VIDEO = "/assets/hero.mp4";

const FEATURES = [
  { key: "sleep", icon: Moon, color: "text-[#8B5CF6]", title: "Sleep & Recovery", body: "Understand your sleep quality, recovery, and readiness." },
  { key: "activity", icon: PersonStanding, color: "text-[#22C55E]", title: "Activity & Performance", body: "Track workouts, steps, VO2 max, and performance trends." },
  { key: "nutrition", icon: Apple, color: "text-[#10B981]", title: "Nutrition & Glucose", body: "See how your nutrition and glucose impact your energy and performance." },
  { key: "stress", icon: HeartPulse, color: "text-[#F97316]", title: "Stress & Readiness", body: "Monitor stress, HRV, and readiness to optimize your day." },
  { key: "ai", icon: Sparkles, color: "text-[#7C6BFF]", title: "AI-Powered Insights", body: "Get personalized recommendations powered by AI." },
  { key: "memory", icon: BookOpen, color: "text-[#3B82F6]", title: "Personal Health Memory", body: "All your data, history, and insights in one secure timeline." },
];

const REFERRAL_OPTIONS = [
  "LinkedIn",
  "Instagram",
  "Twitter / X",
  "Facebook",
  "TikTok",
  "YouTube",
  "Referral from a friend",
  "Search (Google, etc.)",
  "Press / Article",
  "Podcast",
  "Other",
];

const Landing = () => {
  const [form, setForm] = useState({ full_name: "", email: "", referral_source: "" });
  const [formLoading, setFormLoading] = useState(false);
  const [flashKey, setFlashKey] = useState(0);

  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !v.muted;
    v.muted = next;
    setMuted(next);
    if (!next) {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  };

  const submitEarlyAccess = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.referral_source) {
      return toast.error("Please complete all fields");
    }
    try {
      setFormLoading(true);
      await axios.post(`${API}/signups/early-access`, form);
      toast.success("Welcome aboard — you're on the waitlist.");
      setForm({ full_name: "", email: "", referral_source: "" });
      setFlashKey((k) => k + 1);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Invalid submission");
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div data-testid={LANDING.page} className="min-h-screen bg-white text-slate-900 font-sans">
      <Toaster position="top-center" richColors />

      {/* ===== Header: logo on the FAR LEFT, plus nav ===== */}
      <SharedHeader />

      {/* ===== Hero (single column, centered) — no email form here ===== */}
      <section className="relative mx-auto mt-12 sm:mt-20 max-w-4xl px-6 lg:px-10 text-center">
        <HeadlineFader testId={LANDING.heroHeadline} />

        <p
          data-testid={LANDING.heroParagraph}
          className="mt-6 mx-auto max-w-2xl text-base sm:text-lg text-slate-500 leading-relaxed"
        >
          Connect all your wearable data, gain AI-powered insights, and turn information into meaningful daily action.
        </p>

        <a
          href="#early-access"
          data-testid="hero-scroll-cta"
          className="mt-8 inline-flex h-14 items-center justify-center rounded-xl px-8 text-base font-semibold text-white shadow-md transition-transform hover:scale-[1.02]"
          style={{ background: "linear-gradient(90deg, #22B6B0 0%, #4A7AF0 55%, #7A5BFF 100%)" }}
        >
          Join Waitlist
        </a>
      </section>

      {/* ===== Full-width video ===== */}
      <section className="mt-12 sm:mt-16 mx-auto max-w-7xl px-6 lg:px-10">
        <div className="relative overflow-hidden rounded-3xl shadow-md bg-slate-50 aspect-[21/9]">
          <video
            ref={(el) => { videoRef.current = el; }}
            data-testid={LANDING.heroImage}
            autoPlay
            muted={muted}
            loop
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={HERO_VIDEO} type="video/mp4" />
          </video>

          {/* Mute / unmute toggle — positioned over the source video's bottom-left
              watermark so the logo is hidden behind it. */}
          <button
            type="button"
            data-testid="hero-video-sound-toggle"
            onClick={toggleMute}
            aria-label={muted ? "Unmute video" : "Mute video"}
            className="absolute bottom-3 right-12 sm:right-16 md:right-20 grid h-20 w-20 place-items-center rounded-full bg-white text-slate-700 shadow-lg ring-1 ring-black/5 hover:bg-white transition"
          >
            {muted ? <VolumeX className="h-6 w-6" /> : <Volume2 className="h-6 w-6" />}
          </button>
        </div>
      </section>

      {/* ===== Wearables bar (right below the video) ===== */}
      <section
        data-testid={LANDING.wearablesBar}
        className="mt-8 sm:mt-10 mx-auto max-w-7xl px-6 lg:px-10"
      >
        <div className="rounded-3xl bg-white shadow-[0_8px_30px_rgba(15,23,42,0.06)] ring-1 ring-slate-100 py-8 px-6">
          <p className="text-center text-xs sm:text-sm font-semibold tracking-[0.22em] text-slate-700">
            WORKS WITH 100+ WEARABLES &amp; APPS
          </p>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 items-center gap-y-6 gap-x-4 justify-items-center">
            <AppleHealthMark />
            <OuraMark />
            <WhoopMark />
            <FitbitMark />
            <GarminMark />
            <DexcomMark />
            <WithingsMark />
          </div>
        </div>
      </section>

      {/* ===== Features ===== */}
      <section className="mt-20 sm:mt-24 mx-auto max-w-7xl px-6 lg:px-10">
        <h2
          data-testid={LANDING.featuresHeadline}
          className="text-center text-3xl sm:text-4xl lg:text-[2.5rem] font-extrabold tracking-tight"
        >
          One Platform. Every Insight. Endless Possibilities.
        </h2>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
          {FEATURES.map(({ key, icon: Icon, color, title, body }) => (
            <div
              key={key}
              data-testid={LANDING.featureCard(key)}
              className="rounded-2xl bg-white ring-1 ring-slate-100 shadow-[0_4px_18px_rgba(15,23,42,0.04)] p-6 flex flex-col items-center text-center transition-transform hover:-translate-y-1"
            >
              <Icon className={`h-10 w-10 ${color}`} strokeWidth={1.6} />
              <h3 className="mt-5 text-base font-bold text-slate-900">{title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-500">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Early access form — only signup spot ===== */}
      <section
        id="early-access"
        data-testid={LANDING.earlyAccessSection}
        className="mt-20 sm:mt-24 mx-auto max-w-7xl px-6 lg:px-10 scroll-mt-24"
      >
        <div className="rounded-3xl ring-1 ring-[#CFE9FF] bg-gradient-to-br from-[#F4FBFF] via-white to-[#F6F2FF] p-8 sm:p-12 lg:p-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            {/* Left */}
            <div>
              <h2
                data-testid={LANDING.earlyAccessTitle}
                className="text-3xl sm:text-4xl lg:text-[2.5rem] font-extrabold tracking-tight leading-tight"
              >
                Be First to Experience<br />
                <span className="bg-gradient-to-r from-[#22B6B0] to-[#7A5BFF] bg-clip-text text-transparent">
                  Prosper
                </span>
              </h2>

              {/* Social proof replaces the paragraph */}
              <div
                data-testid={LANDING.heroSocialProof}
                className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4"
              >
                <RotatingAvatars count={4} intervalMs={2200} testId="rotating-avatars" />
                <SocialProofFlash refreshKey={flashKey} />
              </div>

              <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
                {[
                  { key: "early", icon: Rocket, color: "text-[#22B6B0]", bg: "bg-[#E6FBF6]", title: "Early Access", body: "Be the first to try new features." },
                  { key: "updates", icon: Bell, color: "text-[#7A5BFF]", bg: "bg-[#EFEBFF]", title: "Exclusive Updates", body: "Get updates and product news." },
                  { key: "shape", icon: Star, color: "text-[#F97316]", bg: "bg-[#FFF1E6]", title: "Shape the Future", body: "Your feedback helps us build." },
                ].map(({ key, icon: Icon, color, bg, title, body }) => (
                  <div
                    key={key}
                    data-testid={LANDING.earlyAccessPerk(key)}
                    className="flex flex-col items-center text-center"
                  >
                    <div className={`grid h-10 w-10 place-items-center rounded-full ${bg}`}>
                      <Icon className={`h-5 w-5 ${color}`} strokeWidth={1.8} />
                    </div>
                    <h4 className="mt-3 text-sm font-bold text-slate-900">{title}</h4>
                    <p className="mt-1 text-xs leading-snug text-slate-500">{body}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right form */}
            <form onSubmit={submitEarlyAccess} className="space-y-4">
              <div className="relative">
                <Input
                  data-testid={LANDING.earlyAccessName}
                  placeholder="Full Name"
                  value={form.full_name}
                  onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                  className="h-14 rounded-xl border-slate-200 bg-white pl-5 pr-12 text-base shadow-sm focus-visible:ring-[#22B6B0]"
                />
                <User className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              </div>

              <div className="relative">
                <Input
                  data-testid={LANDING.earlyAccessEmail}
                  type="email"
                  placeholder="Email Address"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="h-14 rounded-xl border-slate-200 bg-white pl-5 pr-12 text-base shadow-sm focus-visible:ring-[#22B6B0]"
                />
                <Mail className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              </div>

              <div data-testid={LANDING.earlyAccessGoal}>
                <Select
                  value={form.referral_source}
                  onValueChange={(v) => setForm((f) => ({ ...f, referral_source: v }))}
                >
                  <SelectTrigger
                    data-testid={LANDING.earlyAccessGoalTrigger}
                    className="h-14 rounded-xl border-slate-200 bg-white px-5 text-base shadow-sm focus:ring-[#22B6B0]"
                  >
                    <div className="flex flex-col items-start text-left">
                      <span className="text-xs text-slate-400 leading-none">How did you hear about us?</span>
                      <SelectValue
                        placeholder="Select an option"
                        className="text-base text-slate-700"
                      />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {REFERRAL_OPTIONS.map((g) => (
                      <SelectItem key={g} value={g}>
                        {g}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                data-testid={LANDING.earlyAccessSubmit}
                type="submit"
                disabled={formLoading}
                className="h-14 w-full rounded-xl text-base font-semibold text-white shadow-md transition-transform hover:scale-[1.01] disabled:opacity-70"
                style={{
                  background:
                    "linear-gradient(90deg, #22B6B0 0%, #4A7AF0 55%, #7A5BFF 100%)",
                }}
              >
                {formLoading ? "Submitting…" : "Join Waitlist"}
              </Button>

              <p
                data-testid={LANDING.earlyAccessSpamNote}
                className="flex items-center justify-center gap-2 text-sm text-slate-500"
              >
                <Lock className="h-4 w-4" />
                No spam. Just waitlist updates and product news.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <SiteFooter />
    </div>
  );
};

export default Landing;
